import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CalendarOff,
  ClipboardList,
  Clock,
  DoorOpen,
  GraduationCap,
  HeartHandshake,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  MessageSquare,
  Music,
  Send,
  Settings,
  UserPlus,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Images, Newspaper } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ROLE_LABELS, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "direcao", "secretaria", "professor", "aluno", "responsavel"], testId: "nav-dashboard" },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, roles: ["admin", "direcao", "secretaria", "professor"], testId: "nav-agenda" },
  { to: "/admin/alunos", label: "Alunos", icon: UserRound, roles: ["admin", "direcao", "secretaria"], testId: "nav-alunos" },
  { to: "/admin/responsaveis", label: "Responsáveis", icon: HeartHandshake, roles: ["admin", "direcao", "secretaria"], testId: "nav-responsaveis" },
  { to: "/admin/matriculas", label: "Matrículas", icon: ClipboardList, roles: ["admin", "direcao", "secretaria", "professor"], testId: "nav-matriculas" },
  { to: "/admin/interessados", label: "Interessados", icon: UserPlus, roles: ["admin", "direcao", "secretaria"], testId: "nav-interessados" },
  { to: "/admin/solicitacoes", label: "Solicitações", icon: Send, roles: ["admin", "direcao", "secretaria", "professor"], testId: "nav-solicitacoes" },
  { to: "/admin/comunicados", label: "Comunicados", icon: Megaphone, roles: ["admin", "direcao", "secretaria", "professor"], testId: "nav-comunicados" },
  { to: "/admin/whatsapp", label: "WhatsApp Simulador", icon: MessageSquare, roles: ["admin", "direcao", "secretaria"], testId: "nav-whatsapp" },
  { to: "/admin/financeiro", label: "Financeiro", icon: Wallet, roles: ["admin", "direcao", "secretaria"], testId: "nav-financeiro" },
  { to: "/admin/cursos", label: "Cursos", icon: Music, roles: ["admin", "direcao", "secretaria"], testId: "nav-cursos" },
  { to: "/admin/professores", label: "Professores", icon: GraduationCap, roles: ["admin", "direcao", "secretaria"], testId: "nav-professores" },
  { to: "/admin/fotos", label: "Fotos & Álbuns", icon: Images, roles: ["admin", "direcao"], testId: "nav-fotos" },
  { to: "/admin/conteudo", label: "Conteúdo do Site", icon: Newspaper, roles: ["admin", "direcao"], testId: "nav-conteudo" },
  { to: "/admin/disponibilidade", label: "Disponibilidade", icon: Clock, roles: ["admin", "direcao", "secretaria"], testId: "nav-disponibilidade" },
  { to: "/admin/salas", label: "Salas", icon: DoorOpen, roles: ["admin", "direcao", "secretaria"], testId: "nav-salas" },
  { to: "/admin/feriados", label: "Feriados", icon: CalendarOff, roles: ["admin", "direcao", "secretaria"], testId: "nav-feriados" },
  { to: "/admin/usuarios", label: "Usuários", icon: Users, roles: ["admin"], testId: "nav-usuarios" },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings, roles: ["admin"], testId: "nav-configuracoes" },
];

export const Logo = ({ dark = false }) => (
  <span className={`font-heading text-2xl font-extrabold tracking-tighter ${dark ? "text-white" : "text-foreground"}`} data-testid="imj-logo">
    IMJ<span className="text-primary">.</span>
  </span>
);

function NavItems({ onNavigate, unread = 0 }) {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((item) => item.roles.includes(user.role)).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/admin"}
          onClick={onNavigate}
          data-testid={item.testId}
          className={({ isActive }) =>
            `flex items-center gap-3 border-l-2 px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "border-primary text-primary"
                : "border-transparent text-zinc-400 hover:text-white"
            }`
          }
        >
          <item.icon size={18} />
          {item.label}
          {item.to === "/admin/comunicados" && unread > 0 && (
            <span className="ml-auto bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground" data-testid="nav-comunicados-badge">
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (user.role !== "professor") return undefined;
    const fetchCount = () =>
      api.get("/announcements/inbox").then((r) => setUnread(r.data.unread_count)).catch(() => {});
    fetchCount();
    window.addEventListener("announcements-updated", fetchCount);
    return () => window.removeEventListener("announcements-updated", fetchCount);
  }, [user.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-[#0A0A0A] lg:flex" data-testid="sidebar">
        <div className="flex h-16 items-center border-b border-zinc-800 px-6">
          <Logo dark />
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavItems unread={unread} />
        </div>
        <div className="border-t border-zinc-800 p-4 text-xs text-zinc-500">
          Instituto Musical Jacaré
          <br />
          Cabreúva — SP
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-white px-4 sm:px-6" data-testid="topbar">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-[#0A0A0A] p-0 pt-4">
                <div className="px-6 pb-4">
                  <Logo dark />
                </div>
                <NavItems unread={unread} onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="font-heading text-sm font-semibold text-muted-foreground">
              Painel de Gestão
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold leading-tight" data-testid="topbar-user-name">{user.name}</p>
              <p className="text-xs text-muted-foreground" data-testid="topbar-user-role">{ROLE_LABELS[user.role]}</p>
            </div>
            <Button variant="outline" size="icon" onClick={handleLogout} title="Sair" data-testid="logout-button">
              <LogOut size={18} />
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
